import {
  Button, Grid, Typography, FormControlLabel, Checkbox
} from "@mui/material";
import { Form, Formik } from "formik";
import moment, { Moment } from "moment";
import { useTypedSelector } from "../../../../hooks/useTypedSelector";
import { useEffect, useState } from "react";
import {
  useGetProposalDetailsQuery,
  useGetRelationMasterQuery,
  useUpdateProposalFormData,
  useGetBankDetailsByIFSC,
  useGetPincodeMutation,
  useGetRelationMasterAppointeeQuery
} from "../../../../services/Proposal.service";
import {
  allowOnlyAlphabets,
  allowOnlyNumbers,
  toCapitalize,
  allowAddress
} from "../../../../utils/inputUtils";
import DateComp from "../InputComponents/DateComp";
import Select from "../InputComponents/Select";
import TextInput from "../InputComponents/TextInput";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { setProposalData } from "../../../../modules/proposal.slice";
import HeartLoader from "../../../../components/Loader/Loader";
import { useNavigate } from "react-router-dom";
import InputSideEffect from "../InputComponents/InputSideEffect";
import CachedIcon from "@mui/icons-material/Cached";
import BankTextInput from "../InputComponents/BankTextInput";
import { genderOptions } from "../../options.utils";

const NomineeForm = () => {
  const { activeProposal, proposalData } = useTypedSelector(
    (state) => state.proposal
  );
  const { isCKYC, isPortability, product, quote_data } = useGetProposalDetailsQuery();
  const [isMinor, setIsMinor] = useState(() => {
    const dob = proposalData?.nominee_details?.nominee_dob;
    if (dob) {
      return moment().diff(moment(dob, "YYYY-MM-DD"), "years") < 18;
    }
    return false;
  });
  const [appointeeRelationType, setAppointeeRelationType] = useState(() => {
    const dob = proposalData?.nominee_details?.nominee_dob;
    if (dob) {
      return moment().diff(moment(dob, "YYYY-MM-DD"), "years") < 18 ? "appointee" : "nominee";
    }
    return "nominee";
  });
  const { relations } = useGetRelationMasterQuery();
  const { appointeeRelations } =
    useGetRelationMasterAppointeeQuery("appointee", isMinor);

  const { cityOptions: nomineeCityOptions,
    stateOptions: nomineeStateOptions,
    getCityState: getNomineeCityState,
  } = useGetPincodeMutation();
  const {
    cityOptions: appointeeCityOptions,
    stateOptions: appointeeStateOptions,
    getCityState: getAppointeeCityState,
  } = useGetPincodeMutation();

  const {
    cityOptions: commCityOptions,
    stateOptions: commStateOptions,
    getCityState: getCommCityState,
  } = useGetPincodeMutation();

  const {
    cityOptions: appointeeCommCityOptions,
    stateOptions: appointeeCommStateOptions,
    getCityState: getCommAppointeeState,
  } = useGetPincodeMutation();

  const [nominee_ifsccode, setIfscCode] = useState(
    proposalData?.nominee_details?.nominee_ifsccode || ""
  );
  const cleanedRelations = relations?.map((item) => ({
    OPTION_KEY: item.OPTION_KEY?.trim(),
    OPTION_VAL: item.OPTION_VAL?.trim(),
  }));
  const appointeeCleanedRelations = appointeeRelations?.map((item) => ({
    OPTION_KEY: item.OPTION_KEY?.trim(),
    OPTION_VAL: item.OPTION_VAL?.trim(),
  }));

  const SALUTATION_OPTIONS = [
    { OPTION_VAL: "Mr.", OPTION_KEY: "Mr." },
    { OPTION_VAL: "Ms.", OPTION_KEY: "Ms." },
  ];
  const clearAppointeeFields = (setFieldValue: any) => {
    const fields = [
      "appointee_name",
      "appointee_first_name",
      "appointee_last_name",
      "appointee_relation",
      "appointee_relation_data",
      "appointee_dob",
      "appointee_gender",
      "appointee_contact",
      "appointee_address_line_1",
      "appointee_address_line_2",
      "appointee_address_line_3",
      "appointee_pincode",
      "appointee_city",
      "appointee_state",
      "appointee_comm_address_line_1",
      "appointee_comm_address_line_2",
      "appointee_comm_address_line_3",
      "appointee_comm_pincode",
      "appointee_comm_city",
      "appointee_comm_state",
      "appointee_is_comm_same_as_above",
      "appointee_title",
    ];

    fields.forEach((field) => setFieldValue(field, ""));
  };

  const { saveForm, isSaved, isSaving } = useUpdateProposalFormData(() => {
    if (!isPortability) {
      navigate({
        pathname: "/proposal-summary",
        search: window.location.search,
      });
    }
  });
  const navigate = useNavigate();
  const self_dob = proposalData?.proposer_details?.dob;
  const {
    mutate: fetchBankDetails,
    data: bankDetails,
    isPending,
  } = useGetBankDetailsByIFSC();
  const dispatch = useDispatch();
  const noHtmlRegex = /^(?!.*(<|>|&lt;|&gt;)).*$/;

  useEffect(() => {
    if (proposalData?.nominee_details?.nominee_dob) {
      const age = moment().diff(
        moment(proposalData.nominee_details.nominee_dob, "YYYY-MM-DD"),
        "years"
      );
      if (age < 18) {
        setIsMinor(true);
        setAppointeeRelationType("appointee");
      } else {
        setIsMinor(false);
        setAppointeeRelationType("nominee");
      }
    }
  }, [proposalData?.nominee_details?.nominee_dob]);

  useEffect(() => {
    if (nominee_ifsccode.length === 11) {
      fetchBankDetails(nominee_ifsccode);
    }
  }, [nominee_ifsccode, fetchBankDetails]);

  const handleNomineeDobChange = (date: Moment | null, setFieldValue: any) => {
    if (!date) return;
    const age = moment().diff(date, "years");
    if (age < 18) {
      setIsMinor(true);
      setAppointeeRelationType("appointee");
    } else {
      setIsMinor(false);
    }
    clearAppointeeFields(setFieldValue);
  };

  if (activeProposal !== (isCKYC ? 4 : 3)) return null;

  return (
    <>
      <Formik
        initialValues={{
          nominee_name: proposalData?.nominee_details?.nominee_name || "",
          nominee_first_name: proposalData?.nominee_details?.nominee_first_name || "",
          nominee_last_name: proposalData?.nominee_details?.nominee_last_name || "",
          nominee_relation:
            proposalData?.nominee_details?.nominee_relation || "",
          nominee_relation_data:
            proposalData?.nominee_details?.nominee_relation_data || "",
          nominee_dob: proposalData?.nominee_details?.nominee_dob || "",
          nominee_contact: proposalData?.nominee_details?.nominee_contact || "",
          others_nominee_relation:
            proposalData?.nominee_details?.others_nominee_relation || "",
          nominee_bankAccountNo:
            proposalData?.nominee_details?.nominee_bankAccountNo || "",
          nominee_ifsccode:
            proposalData?.nominee_details?.nominee_ifsccode || "",
          nominee_bank: proposalData?.nominee_details?.nominee_bank || "",
          nominee_branchName:
            proposalData?.nominee_details?.nominee_branchName || "",
          appointee_first_name: proposalData?.nominee_details?.appointee_first_name || "",
          appointee_last_name: proposalData?.nominee_details?.appointee_last_name || "",
          appointee_relation:
            proposalData?.nominee_details?.appointee_relation || "",
          appointee_dob: proposalData?.nominee_details?.appointee_dob || "",
          nominee_email: proposalData?.nominee_details?.nominee_email || "",
          nominee_title: proposalData?.nominee_details?.nominee_title || "",
          nominee_pincode: proposalData?.nominee_details?.nominee_pincode || "",
          nominee_city: proposalData?.nominee_details?.nominee_city || "",
          nominee_state: proposalData?.nominee_details?.nominee_state || "",
          nominee_address_line_1:
            proposalData?.nominee_details?.nominee_address_line_1 || "",
          nominee_address_line_2:
            proposalData?.nominee_details?.nominee_address_line_2 || "",
          nominee_address_line_3:
            proposalData?.nominee_details?.nominee_address_line_3 || "",
          nominee_is_comm_same_as_above:
            proposalData?.nominee_details?.nominee_is_comm_same_as_above ?? true,
          nominee_comm_pincode: proposalData?.nominee_details?.nominee_comm_pincode || "",
          nominee_comm_address_line_1: proposalData?.nominee_details?.nominee_comm_address_line_1 || "",
          nominee_comm_address_line_2: proposalData?.nominee_details?.nominee_comm_address_line_2 || "",
          nominee_comm_city: proposalData?.nominee_details?.nominee_comm_city || "",
          nominee_comm_state: proposalData?.nominee_details?.nominee_comm_state || "",
          nominee_comm_address_line_3: proposalData?.nominee_details?.nominee_comm_address_line_3 || "",
          nominee_beneficiary_name: proposalData?.nominee_details?.nominee_beneficiary_name || "",
          appointee_gender: proposalData?.nominee_details?.appointee_gender || "",
          nominee_gender: proposalData?.nominee_details?.nominee_gender || "",
          appointee_name: proposalData?.nominee_details?.appointee_name || "",
          appointee_contact: proposalData?.nominee_details?.appointee_contact || "",
          appointee_address_line_1: proposalData?.nominee_details?.appointee_address_line_1 || "",
          appointee_address_line_2: proposalData?.nominee_details?.appointee_address_line_2 || "",
          appointee_address_line_3: proposalData?.nominee_details?.appointee_address_line_3 || "",
          appointee_pincode: proposalData?.nominee_details?.appointee_pincode || "",
          appointee_city: proposalData?.nominee_details?.appointee_city || "",
          appointee_state: proposalData?.nominee_details?.appointee_state || "",
          appointee_is_comm_same_as_above:
            proposalData?.nominee_details?.appointee_is_comm_same_as_above ?? true,
          appointee_comm_address_line_1: proposalData?.nominee_details?.appointee_comm_address_line_1 || "",
          appointee_comm_address_line_2: proposalData?.nominee_details?.appointee_comm_address_line_2 || "",
          appointee_comm_address_line_3: proposalData?.nominee_details?.appointee_comm_address_line_3 || "",
          appointee_comm_pincode: proposalData?.nominee_details?.appointee_comm_pincode || "",
          appointee_comm_city: proposalData?.nominee_details?.appointee_comm_city || "",
          appointee_comm_state: proposalData?.nominee_details?.appointee_comm_state || "",
          appointee_title: proposalData?.nominee_details?.appointee_title || "",
        }}
        validationSchema={yup.object().shape({
          nominee_first_name: yup.string().when([], {
            is: () => product?.INSURANCE_SLUG === "care",
            then: (schema) =>
              schema
                .required("Nominee first name is required")
                .matches(noHtmlRegex, "HTML or script tags are not allowed")
                .test(
                  "min-two-chars",
                  "Nominee first name must contain at least 2 characters",
                  (val) => !!val && val.trim().length >= 2
                ),
            otherwise: (schema) => schema.notRequired(),
          }),

          nominee_last_name: yup.string().when([], {
            is: () => product?.INSURANCE_SLUG === "care",
            then: (schema) =>
              schema
                .required("Nominee last name is required")
                .matches(noHtmlRegex, "HTML or script tags are not allowed"),
            otherwise: (schema) => schema.notRequired(),
          }),

          appointee_first_name: yup.string().when("nominee_dob", (dob, schema) => {
            if (
              dob &&
              product?.INSURANCE_SLUG === "care" &&
              moment().diff(moment(dob, "YYYY-MM-DD"), "years") < 18
            ) {
              return schema
                .required("Appointee first name is required")
                .matches(noHtmlRegex, "HTML or script tags are not allowed")
                .test(
                  "min-two-chars",
                  "Appointee first name must contain at least 2 characters",
                  (val) => !!val && val.trim().length >= 2
                );
            }

            return schema.matches(
              noHtmlRegex,
              "HTML or script tags are not allowed"
            );
          }),
          appointee_last_name: yup.string().when("nominee_dob", (dob, schema) => {
            if (
              dob &&
              product?.INSURANCE_SLUG === "care" &&
              moment().diff(moment(dob, "YYYY-MM-DD"), "years") < 18
            ) {
              return schema
                .required("Appointee last name is required")
                .matches(noHtmlRegex, "HTML or script tags are not allowed")
            }

            return schema.matches(
              noHtmlRegex,
              "HTML or script tags are not allowed"
            );
          }),
          nominee_relation: yup
            .string()
            .required("Nominee relation is required"),
          nominee_dob: yup
            .string()
            .required("Nominee dob is required")
            .test(
              "nominee_dob",
              "Parents must be at least 18 years older or children must be 18 or younger",
              function (val) {
                if (
                  self_dob &&
                  (this.parent.nominee_relation?.toLowerCase() === "father" ||
                    this.parent.nominee_relation?.toLowerCase() === "mother" ||
                    this.parent.nominee_relation?.toLowerCase() ===
                    "grand mother" ||
                    this.parent.nominee_relation?.toLowerCase() ===
                    "grand father")
                ) {
                  return (
                    moment(self_dob, "YYYY-MM-DD").diff(
                      moment(val, "YYYY-MM-DD"),
                      "years"
                    ) >= 18
                  );
                }
                if (
                  self_dob &&
                  (this.parent.nominee_relation?.toLowerCase() === "son" ||
                    this.parent.nominee_relation?.toLowerCase() === "daughter")
                ) {
                  const selfDOB = moment(self_dob, "YYYY-MM-DD");
                  const childDOB = moment(val, "YYYY-MM-DD");

                  // Calculate the date when you turned 18
                  const eligibleDateToHaveChild = moment(selfDOB).add(
                    18,
                    "years"
                  );

                  // If the child is born before you turned 18 — INVALID
                  return childDOB.isSameOrAfter(eligibleDateToHaveChild);
                }

                return true;
              }
            ),
          nominee_email: yup
            .string()
            .email("Enter a valid email")
            .matches(noHtmlRegex, "HTML or script tags are not allowed"),
          ...(product?.INSURANCE_SLUG === "care" && {
            nominee_bankAccountNo: yup
              .string()
              .required("Bank account number is required")
              .matches(noHtmlRegex, "HTML or script tags are not allowed"),

            nominee_ifsccode: yup
              .string()
              .required("IFSC code is required")
              .matches(noHtmlRegex, "HTML or script tags are not allowed"),

            nominee_beneficiary_name: yup
              .string()
              .required("Beneficiary name is required")
              .matches(noHtmlRegex, "HTML or script tags are not allowed"),

            nominee_pincode: yup
              .string()
              .required("Pincode is required")
              .length(6, "Pincode must be 6 digits"),

            nominee_city: yup
              .string()
              .required("City is required")
              .matches(noHtmlRegex, "HTML or script tags are not allowed"),

            nominee_state: yup
              .string()
              .required("State is required")
              .matches(noHtmlRegex, "HTML or script tags are not allowed"),

            nominee_address_line_1: yup
              .string()
              .required("Address is required")
              .matches(noHtmlRegex, "HTML or script tags are not allowed"),

            nominee_address_line_2: yup
              .string()
              .required("Address is required")
              .matches(noHtmlRegex, "HTML or script tags are not allowed"),

            nominee_address_line_3: yup
              .string()
              .matches(noHtmlRegex, "HTML or script tags are not allowed"),

            nominee_comm_address_line_1: yup.string().when(
              "nominee_is_comm_same_as_above",
              {
                is: (val: boolean) => !val,
                then: (schema) =>
                  schema
                    .required("Address is required")
                    .max(60, "Address should not be more than 60 characters")
                    .matches(noHtmlRegex, "HTML or script tags are not allowed"),
              }
            ),

            nominee_comm_address_line_2: yup.string().when(
              "nominee_is_comm_same_as_above",
              {
                is: (val: boolean) => !val,
                then: (schema) =>
                  schema
                    .required("Address is required")
                    .max(60, "Address should not be more than 60 characters")
                    .matches(noHtmlRegex, "HTML or script tags are not allowed"),
              }
            ),

            nominee_comm_address_line_3: yup
              .string()
              .matches(noHtmlRegex, "HTML or script tags are not allowed"),

            nominee_comm_pincode: yup.string().when(
              "nominee_is_comm_same_as_above",
              {
                is: (val: boolean) => !val,
                then: (schema) =>
                  schema
                    .required("Pincode is required")
                    .matches(/^[1-9][0-9]{5}$/, "Please enter a valid pincode"),
              }
            ),

            nominee_comm_city: yup.string().when(
              "nominee_is_comm_same_as_above",
              {
                is: (val: boolean) => !val,
                then: (schema) =>
                  schema
                    .required("City is required")
                    .max(60, "City should not be more than 60 characters")
                    .matches(noHtmlRegex, "HTML or script tags are not allowed"),
              }
            ),

            nominee_comm_state: yup.string().when(
              "nominee_is_comm_same_as_above",
              {
                is: (val: boolean) => !val,
                then: (schema) =>
                  schema
                    .required("State is required")
                    .max(60, "State should not be more than 60 characters")
                    .matches(noHtmlRegex, "HTML or script tags are not allowed"),
              }
            ),

            nominee_email: yup
              .string()
              .email("Enter a valid email")
              .matches(noHtmlRegex, "HTML or script tags are not allowed"),
          }),

          appointee_email: yup
            .string()
            .email("Enter a valid email")
            .matches(noHtmlRegex, "HTML or script tags are not allowed"),
          appointee_relation: yup.string().when("nominee_dob", (dob, schema) => {
            if (dob && product?.INSURANCE_SLUG === "care" &&
              moment().diff(moment(dob, "YYYY-MM-DD"), "years") < 18) {
              return schema.required("Appointee relation is required");
            }
            return schema;
          }),

          appointee_dob: yup.string().when("nominee_dob", (dob, schema) => {
            if (dob && product?.INSURANCE_SLUG === "care" &&
              moment().diff(moment(dob, "YYYY-MM-DD"), "years") < 18) {
              return schema.required("Appointee DOB is required");
            }
            return schema;
          }),

          appointee_pincode: yup.string().when("nominee_dob", (dob, schema) => {
            if (dob && product?.INSURANCE_SLUG === "care" &&
              moment().diff(moment(dob, "YYYY-MM-DD"), "years") < 18) {
              return schema
                .required("Appointee pincode is required")
                .length(6, "Pincode must be 6 digits");
            }
            return schema;
          }),

          appointee_city: yup.string().when("nominee_dob", (dob, schema) => {
            if (dob && product?.INSURANCE_SLUG === "care" &&
              moment().diff(moment(dob, "YYYY-MM-DD"), "years") < 18) {
              return schema.required("Appointee city is required");
            }
            return schema;
          }),

          appointee_address_line_1: yup.string().when("nominee_dob", (dob, schema) => {
            if (dob && product?.INSURANCE_SLUG === "care" &&
              moment().diff(moment(dob, "YYYY-MM-DD"), "years") < 18) {
              return schema.required("Appointee address line 1 is required");
            }
            return schema;
          }),

          appointee_address_line_2: yup.string().when("nominee_dob", (dob, schema) => {
            if (dob && product?.INSURANCE_SLUG === "care" &&
              moment().diff(moment(dob, "YYYY-MM-DD"), "years") < 18) {
              return schema.required("Appointee address line 2 is required");
            }
            return schema;
          }),

          appointee_state: yup.string().when("nominee_dob", (dob, schema) => {
            if (dob && product?.INSURANCE_SLUG === "care" &&
              moment().diff(moment(dob, "YYYY-MM-DD"), "years") < 18) {
              return schema.required("Appointee state is required");
            }
            return schema;
          }),
          appointee_contact: yup.string().when("nominee_dob", (dob, schema) => {
            if (dob && product?.INSURANCE_SLUG === "care" &&
              moment().diff(moment(dob, "YYYY-MM-DD"), "years") < 18) {
              return schema.required("Appointee contact is required").min(10, "Contact must be 10 digits").max(10, "Contact must be 10 digits");
            }
            return schema;
          }),
          nominee_contact: yup.string().required("Nominee contact is required"),
        })}
        onSubmit={async (values) => {
          let nomineeDetails = { ...values };

          // If NOT care product
          if (product?.INSURANCE_SLUG !== "care") {
            delete nomineeDetails.nominee_first_name;
            delete nomineeDetails.nominee_last_name;
            delete nomineeDetails.nominee_gender;

            delete nomineeDetails.appointee_first_name;
            delete nomineeDetails.appointee_last_name;
            delete nomineeDetails.appointee_gender;

            // optional if not needed
            delete nomineeDetails.appointee_contact;
            delete nomineeDetails.appointee_address_line_1;
            delete nomineeDetails.appointee_address_line_2;
            delete nomineeDetails.appointee_address_line_3;
            delete nomineeDetails.appointee_pincode;
            delete nomineeDetails.appointee_city;
            delete nomineeDetails.appointee_state;
          }

          // If care product
          if (product?.INSURANCE_SLUG === "care") {
            delete nomineeDetails.nominee_name;
            delete nomineeDetails.appointee_name;
          }

          saveForm({
            ...proposalData,
            nominee_details: nomineeDetails,
            from_screen: "nominee",
          });

          dispatch(
            setProposalData({
              nominee_details: {
                ...proposalData,
                ...nomineeDetails,
                nominee_ifsccode,
                nominee_bank: bankDetails?.BANK,
                nominee_branchName: bankDetails?.BRANCH,
              },
            })
          );
        }}
      >
        {({ submitForm, values, setFieldValue }) => (
          <Form>
            <Grid container spacing={2}>
              {isSaving && <HeartLoader message={""} />}
              {/* Nominee Fields */}
              {product?.INSURANCE_SLUG === "care" && (
                <>
                  <Grid item xs={12} md={6}>
                    <Select
                      label="Title"
                      name="nominee_title"
                      options={SALUTATION_OPTIONS}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextInput
                      label="Nominee First Name"
                      name="nominee_first_name"
                      onInput={(e) => {
                        allowOnlyAlphabets(e);
                        toCapitalize(e);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextInput
                      label="Nominee Last Name"
                      name="nominee_last_name"
                      onInput={(e) => {
                        allowOnlyAlphabets(e);
                        toCapitalize(e);
                      }}
                    />
                  </Grid>
                </>
              )
              }
              {product?.INSURANCE_SLUG !== "care" && (
                <Grid item xs={12} md={6}>
                  <>
                    <TextInput
                      label="Nominee Name"
                      name="nominee_name"
                      onInput={(e) => {
                        allowOnlyAlphabets(e);
                        toCapitalize(e);
                      }}
                    />
                  </>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <InputSideEffect
                  dependencies={[values.nominee_relation]}
                  effect={() => {
                    const selected = cleanedRelations?.find(
                      (item) => item.OPTION_KEY === values.nominee_relation
                    );

                    setFieldValue(
                      "nominee_relation_data",
                      selected?.OPTION_VAL || ""
                    );
                  }}
                >
                  <Select
                    label="Nominee's Relation to Proposer"
                    name="nominee_relation"
                    options={cleanedRelations || []}
                  />
                </InputSideEffect>

              </Grid>
              {values.nominee_relation_data === "Others" &&
                product?.INSURANCE_SLUG === "niva_bupa" && (
                  <Grid item xs={12} md={6}>
                    <TextInput
                      label="Nominee's Relation"
                      name="others_nominee_relation"
                      maxLength={30}
                      onInput={(e) => {
                        allowOnlyAlphabets(e);
                      }}
                    />
                  </Grid>
                )}

              <Grid item xs={12} md={6}>
                <DateComp
                  label="Nominee Dob"
                  name="nominee_dob"
                  minDate={moment().subtract(60, "years")}
                  maxDate={moment()}
                  onChange={(date) => {
                    handleNomineeDobChange(date, setFieldValue);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Nominee Contact"
                  name="nominee_contact"
                  onInput={allowOnlyNumbers}
                  maxLength={10}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  name="nominee_email"
                  label="Nominee Email"
                  maxLength={30}
                />
              </Grid>
              {product?.INSURANCE_SLUG === "care" && (
                <Grid item xs={12} md={6}>
                  <Select
                    label="Gender"
                    name="nominee_gender"
                    options={genderOptions}
                  />
                </Grid>)}
              {product?.INSURANCE_SLUG !== "care" && (
                <>
                  {moment().diff(moment(values.nominee_dob, "YYYY-MM-DD"), "year") <
                    18 && (
                      <>
                        <Grid item xs={12} md={6}>
                          <Select
                            label="Title"
                            name="appointee_title"
                            options={SALUTATION_OPTIONS}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextInput
                            label="Appointee Name"
                            name="appointee_name"
                            onInput={(e) => {
                              allowOnlyAlphabets(e);
                              toCapitalize(e);
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <InputSideEffect
                            dependencies={[values.appointee_relation]}
                            effect={() => {
                              setFieldValue(
                                "appointee_relation_data",
                                appointeeCleanedRelations?.find(
                                  (item) =>
                                    item.OPTION_KEY === values.appointee_relation
                                )?.OPTION_VAL || ""
                              );
                            }}
                          >
                            <Select
                              label="Appointee's Relation to Nominee"
                              name="appointee_relation"
                              options={appointeeCleanedRelations || []}
                            />
                          </InputSideEffect>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DateComp
                            label="Appointee Dob"
                            name="appointee_dob"
                            minDate={moment().subtract(60, "years")}
                            maxDate={moment().subtract(18, "years")}
                          />
                        </Grid>
                      </>
                    )}
                </>)}
              {product?.INSURANCE_SLUG === "care" && (
                <>
                  <Grid item xs={12}>
                    <Typography color="text.secondary" fontWeight="500">
                      Nominee Address
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextInput
                      label="Address Line 1"
                      name="nominee_address_line_1"
                      maxLength={60}
                      onInput={allowAddress}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextInput
                      label="Address Line 2"
                      name="nominee_address_line_2"
                      maxLength={60}
                      onInput={allowAddress}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextInput
                      label="Address Line 3"
                      name="nominee_address_line_3"
                      maxLength={60}
                      onInput={allowAddress}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputSideEffect
                      dependencies={[values.nominee_pincode, quote_data?.COMPANY_SLUG]}
                      effect={() => {
                        if (values.nominee_pincode && values.nominee_pincode.length === 6)
                          getNomineeCityState({
                            pincode: values.nominee_pincode,
                            company_alias: quote_data?.COMPANY_SLUG,
                          });
                      }}
                    >
                      <TextInput
                        label="Pin code"
                        name="nominee_pincode"
                        onInput={allowOnlyNumbers}
                        maxLength={6}
                      />
                    </InputSideEffect>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Select
                      options={nomineeCityOptions || []}
                      label="City"
                      name="nominee_city"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputSideEffect
                      dependencies={[nomineeStateOptions]}
                      effect={() => {
                        if (nomineeStateOptions && nomineeStateOptions.length) {
                          setFieldValue("nominee_state", nomineeStateOptions[0].OPTION_VAL);
                        }
                      }}
                    >
                      <TextInput
                        label="State"
                        name="nominee_state"
                        readOnly
                      />
                    </InputSideEffect>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Is Communication Address same as above address?"
                      control={<Checkbox />}
                      onChange={(e, checked) => {
                        setFieldValue("nominee_is_comm_same_as_above", checked);
                      }}
                      checked={Boolean(values.nominee_is_comm_same_as_above)}
                    />
                  </Grid>
                  {!values.nominee_is_comm_same_as_above && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextInput
                          label="Address Line 1"
                          name="nominee_comm_address_line_1"
                          maxLength={60}
                          onInput={allowAddress}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextInput
                          label="Address Line 2"
                          name="nominee_comm_address_line_2"
                          maxLength={60}
                          onInput={allowAddress}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextInput
                          label="Address Line 3"
                          name="nominee_comm_address_line_3"
                          maxLength={60}
                          onInput={allowAddress}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <InputSideEffect
                          dependencies={[values.nominee_comm_pincode]}
                          effect={() => {
                            if (
                              values.nominee_comm_pincode &&
                              values.nominee_comm_pincode.length === 6
                            )
                              getCommCityState({
                                pincode: values.nominee_comm_pincode,
                                company_alias: quote_data?.COMPANY_SLUG,
                              });
                          }}
                        >
                          <TextInput
                            label="Pincode"
                            name="nominee_comm_pincode"
                            onInput={allowOnlyNumbers}
                            maxLength={6}
                          />
                        </InputSideEffect>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Select
                          options={commCityOptions || []}
                          label="City"
                          name="nominee_comm_city"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <InputSideEffect
                          dependencies={[commStateOptions]}
                          effect={() => {
                            if (commStateOptions && commStateOptions.length) {
                              setFieldValue(
                                "nominee_comm_state",
                                commStateOptions[0].OPTION_VAL
                              );
                            }
                          }}
                        >
                          <TextInput label="State" name="nominee_comm_state" readOnly />
                        </InputSideEffect>
                      </Grid>
                    </>
                  )}
                </>
              )}
              {product?.INSURANCE_SLUG === "care" && (
                <>
                  <Grid item xs={12}>
                    <Typography color="text.secondary" fontWeight="500">
                      Nominee Bank Details
                      {isPending && (
                        <div className="center">
                          <CachedIcon className="loading-icon" />
                        </div>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextInput
                      name="nominee_bankAccountNo"
                      label="Bank Account Number"
                      maxLength={30}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <BankTextInput
                      name="nominee_ifsccode"
                      label="IFSC Code"
                      maxLength={11}
                      externalValue={nominee_ifsccode}
                      onChange={(e) => setIfscCode(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextInput
                      label="Beneficiary Name"
                      name="nominee_beneficiary_name"
                    />
                  </Grid>
                </>
              )}
              {product?.INSURANCE_SLUG === "care" && (
                <>
                  {moment().diff(moment(values.nominee_dob, "YYYY-MM-DD"), "year") <
                    18 && (
                      <>
                        <Grid item xs={12}>
                          <Typography color="text.secondary" fontWeight="500">
                            Appointee Details
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Select
                            label="Title"
                            name="appointee_title"
                            options={SALUTATION_OPTIONS}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextInput
                            label="Appointee first Name"
                            name="appointee_first_name"
                            onInput={(e) => {
                              allowOnlyAlphabets(e);
                              toCapitalize(e);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextInput
                            label="Appointee Last Name"
                            name="appointee_last_name"
                            onInput={(e) => {
                              allowOnlyAlphabets(e);
                              toCapitalize(e);
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <InputSideEffect
                            dependencies={[values.appointee_relation]}
                            effect={() => {
                              setFieldValue(
                                "appointee_relation_data",
                                appointeeCleanedRelations?.find(
                                  (item) =>
                                    item.OPTION_KEY === values.appointee_relation
                                )?.OPTION_VAL || ""
                              );
                            }}
                          >
                            <Select
                              label="Appointee's Relation to Nominee"
                              name="appointee_relation"
                              options={appointeeCleanedRelations || []}
                            />
                          </InputSideEffect>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DateComp
                            label="Appointee Dob"
                            name="appointee_dob"
                            minDate={moment().subtract(60, "years")}
                            maxDate={moment().subtract(18, "years")}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Select
                            label="Appointee Gender"
                            name="appointee_gender"
                            options={genderOptions}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextInput
                            label="Appointee Contact"
                            name="appointee_contact"
                            onInput={allowOnlyNumbers}
                            maxLength={10}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextInput
                            label="Address Line 1"
                            name="appointee_address_line_1"
                            maxLength={60}
                            onInput={allowAddress}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextInput
                            label="Address Line 2"
                            name="appointee_address_line_2"
                            maxLength={60}
                            onInput={allowAddress}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextInput
                            label="Address Line 3"
                            name="appointee_address_line_3"
                            maxLength={60}
                            onInput={allowAddress}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <InputSideEffect
                            dependencies={[values.appointee_pincode, quote_data?.COMPANY_SLUG]}
                            effect={() => {
                              if (values.appointee_pincode && values.appointee_pincode.length === 6)
                                getAppointeeCityState({
                                  pincode: values.appointee_pincode,
                                  company_alias: quote_data?.COMPANY_SLUG,
                                });
                            }}
                          >
                            <TextInput
                              label="Pincode"
                              name="appointee_pincode"
                              onInput={allowOnlyNumbers}
                              maxLength={6}
                            />
                          </InputSideEffect>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Select
                            options={appointeeCityOptions || []}
                            label="City"
                            name="appointee_city"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <InputSideEffect
                            dependencies={[appointeeStateOptions]}
                            effect={() => {
                              if (appointeeStateOptions && appointeeStateOptions.length) {
                                setFieldValue("appointee_state", appointeeStateOptions[0].OPTION_VAL);
                              }
                            }}
                          >
                            <TextInput
                              label="State"
                              name="appointee_state"
                              readOnly
                            />
                          </InputSideEffect>
                        </Grid>
                        <Grid item xs={12}>
                          <FormControlLabel
                            label="Is Communication Address same as above address?"
                            control={<Checkbox />}
                            onChange={(e, checked) => {
                              setFieldValue("appointee_is_comm_same_as_above", checked);
                            }}
                            checked={Boolean(values.appointee_is_comm_same_as_above)}
                          />
                        </Grid>
                        {!values.appointee_is_comm_same_as_above && (
                          <>
                            <Grid item xs={12} md={6}>
                              <TextInput
                                label="Address Line 1"
                                name="appointee_comm_address_line_1"
                                maxLength={60}
                                onInput={allowAddress}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextInput
                                label="Address Line 2"
                                name="appointee_comm_address_line_2"
                                maxLength={60}
                                onInput={allowAddress}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextInput
                                label="Address Line 3"
                                name="appointee_comm_address_line_3"
                                maxLength={60}
                                onInput={allowAddress}
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <InputSideEffect
                                dependencies={[values.appointee_comm_pincode]}
                                effect={() => {
                                  if (
                                    values.appointee_comm_pincode &&
                                    values.appointee_comm_pincode.length === 6
                                  )
                                    getCommAppointeeState({
                                      pincode: values.appointee_comm_pincode,
                                      company_alias: quote_data?.COMPANY_SLUG,
                                    });
                                }}
                              >
                                <TextInput
                                  label="Pincode"
                                  name="appointee_comm_pincode"
                                  onInput={allowOnlyNumbers}
                                  maxLength={6}
                                />
                              </InputSideEffect>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Select
                                options={appointeeCommCityOptions || []}
                                label="City"
                                name="appointee_comm_city"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <InputSideEffect
                                dependencies={[appointeeCommStateOptions]}
                                effect={() => {
                                  if (appointeeCommStateOptions && appointeeCommStateOptions.length) {
                                    setFieldValue(
                                      "appointee_comm_state",
                                      appointeeCommStateOptions[0].OPTION_VAL
                                    );
                                  }
                                }}
                              >
                                <TextInput label="State" name="appointee_comm_state" readOnly />
                              </InputSideEffect>
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                </>
              )}
              {product?.INSURANCE_SLUG === "niva_bupa" && (
                <>
                  <Grid item xs={12}>
                    <Typography color="text.secondary" fontWeight="500">
                      Nominee Bank Details
                      {isPending && (
                        <div className="center">
                          <CachedIcon className="loading-icon" />
                        </div>
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextInput
                      name="nominee_bankAccountNo"
                      label="Bank Account Number"
                      maxLength={30}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <BankTextInput
                      name="nominee_ifsccode"
                      label="IFSC Code"
                      maxLength={11}
                      externalValue={nominee_ifsccode}
                      onChange={(e) => setIfscCode(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <BankTextInput
                      label="Bank Name"
                      name="nominee_bank"
                      externalValue={bankDetails?.BANK}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <BankTextInput
                      name="nominee_branchName"
                      label="Branch Name"
                      externalValue={bankDetails?.BRANCH}
                    />
                  </Grid>
                </>
              )}
              <Grid
                item
                xs={12}
                justifyContent="center"
                display={"flex"}
                my={1}
              >
                <Button
                  size="large"
                  variant="contained"
                  onClick={() => {
                    submitForm();
                  }}
                  sx={{
                    background: "#FF6024",
                    "&:hover": { background: "#f26d02" },
                  }}
                >
                  {isPortability
                    ? "Proceed to Portability"
                    : "Proceed to Review & Pay"}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
};

const styles = `
@keyframes spin {
0% { transform: rotate(0deg); }
100% { transform: rotate(360deg); }
}
.loading-icon {
font-size: 24px; /* Adjust size as needed */
animation: spin 1s linear infinite;
}
.center {
display: flex;
justify-content: center;
align-items: center;
}
`;

export default NomineeForm;

export const bankAccountType = [
  {
    OPTION_KEY: "Savings",
    OPTION_VAL: "Savings",
  },
  {
    OPTION_KEY: "Current",
    OPTION_VAL: "Current",
  },
];
